import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LogoutIcon, LogoutModalIcon } from '@icons';
import { LongLogoBlack } from '@images';

import { Avatar, ConfirmationModal, SidebarItem } from '@components';
import { AUTH } from '@constants/routes';
import { ADMIN_MENU_ITEMS } from '@constants/sideMenu';
import { resetAdminState } from '@features/admin/slices/adminSlice';
import { useLazyLogoutQuery } from '@features/authentication/queries/AuthQuery';
import useCheckPermission from '@hooks/checkPermission';
import useNotify from '@hooks/notify';
import {
  setCurrentUser,
  setRolesAndPermissions
} from '@layouts/main-header/userSlice';
import { ApiError } from '@types';
import { getPhoneNumberWith91 } from '@utils/generic';
import { clearAllLocalStorageItems, getLocalStorageItem } from '@utils/storage';

import theme from '../../../themes/colors';

const AdminSideNavBar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { createErrorAlert } = useNotify();
  const { checkPermission } = useCheckPermission();

  const { currentUser } = useSelector((state: any) => state.user);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState<boolean>(false);

  const userId = getLocalStorageItem('userId');

  const [logout, { isLoading: isLogoutLoading }] = useLazyLogoutQuery();
  const onClickLogout = () => {
    setIsLogoutModalOpen(true);
  };

  const onCancelLogout = () => {
    setIsLogoutModalOpen(false);
  };

  const handleLogoutConfirm = async () => {
    const res = await logout(userId);
    if ('data' in res) {
      dispatch(setCurrentUser({ user: {} }));
      dispatch(setRolesAndPermissions({ permissions: {} }));
      dispatch(resetAdminState());
      clearAllLocalStorageItems();
      navigate(AUTH.LOGIN);
      sessionStorage.removeItem('currentPageOrg');
      sessionStorage.removeItem('currentPageIndividual');
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(error.data?.message);
    }
  };

  const permittedSideNavItems = ADMIN_MENU_ITEMS.filter((navItem) => {
    if (navItem.permissions) {
      return navItem.permissions.some((permission) =>
        checkPermission([permission])
      );
    }
    return true; // Include items without `permissions`
  });

  return (
    <div className="group absolute left-0 top-0 z-50 flex h-full w-56 flex-col justify-between bg-theme px-2 shadow-sm transition-all duration-300 ease-in-out">
      <div className="mt-6 flex flex-col gap-3 py-3">
        {permittedSideNavItems.map((item: any) => (
          <SidebarItem key={item.id} item={item} />
        ))}
      </div>
      <div className="relative">
        <div
          role="presentation"
          className="flex cursor-pointer items-center gap-3 py-3 pl-8"
          onClick={onClickLogout}
        >
          <div>
            <LogoutIcon />
          </div>
          <div className="truncate text-sm font-medium text-content">
            Logout
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 bg-theme px-4 py-5">
          <div className="flex items-center gap-2">
            <Avatar
              name={currentUser?.name}
              className="size-10 !border-0 text-theme"
              bgColor={theme.blueGrey}
            />
            <div className="max-w-43 truncate break-words text-sm font-medium text-content">
              {currentUser?.name}
              <div className="flex items-center justify-between text-xs font-normal">
                <div>{currentUser?.role_name}</div>
                <div>{getPhoneNumberWith91(currentUser?.mobile)}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 px-6 pb-8">
          <LongLogoBlack className="h-8 w-auto" />
        </div>
      </div>
      {isLogoutModalOpen && (
        <ConfirmationModal
          isOpen={isLogoutModalOpen}
          ModalIcon={LogoutModalIcon}
          title="Logging Out?"
          message="Are you sure you want to log out?"
          confirmButtonStyle="text-primary"
          handleConfirm={handleLogoutConfirm}
          handleCancel={onCancelLogout}
          className="w-78"
          isConfirmButtonLoading={isLogoutLoading}
        />
      )}
    </div>
  );
};

export default AdminSideNavBar;
